import { useMemo, useEffect, useReducer } from 'react'
import { Scope, TranslateOptions } from 'i18n-js'
import intl, {
  TranslateModelOptions,
  AnyDate,
  FormatDateTimeScope,
  FormatDateScope,
  FormatTimeScope,
} from '@/lib/intl'
import useLocale from '@/lib/hooks/useLocale'
import useIndexPageContext from '@/components/IndexPage/Context/useContext'

export type Translate = {
  (scope: Scope, options?: TranslateOptions | undefined): string

  model(scope: Scope, options?: (TranslateModelOptions & TranslateOptions) | undefined): string

  formatDateTime: (typeof intl)['formatDateTime']
  formatDate: (typeof intl)['formatDate']
  formatTime: (typeof intl)['formatTime']
  formatTimeDuration: (typeof intl)['formatTimeDuration']
}

const useIntl = (baseScope?: string) => {
  const locale = useLocale()
  const { model } = useIndexPageContext()
  const [key, updateKey] = useReducer((key: boolean) => !key, false)

  if (import.meta.hot) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => intl.onHotReload(updateKey), [])
  }

  return useMemo<Translate>(() => {
    const withBaseScope = (scope: Scope) => {
      if (!baseScope) {
        return scope
      } else if (typeof scope === 'string' && scope.startsWith('.')) {
        return `${baseScope}${scope}`
      } else if (Array.isArray(scope) && scope[0]?.startsWith('.')) {
        return [...baseScope.split('.'), ...scope.slice(1)]
      }

      return scope
    }

    const translate = (...[scope, options]: Parameters<typeof intl.t>) =>
      intl.t(withBaseScope(scope), options)

    translate.model = (...[scope, options]: Parameters<typeof intl.model>) =>
      intl.model(withBaseScope(scope), model ? { ...options, model } : options)

    translate.formatDateTime = intl.formatDateTime.bind(intl)
    translate.formatDate = intl.formatDate.bind(intl)
    translate.formatTime = intl.formatTime.bind(intl)
    translate.formatTimeDuration = intl.formatTimeDuration.bind(intl)

    return translate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, baseScope, locale, model])
}

export default useIntl
